import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ArrowRight, CheckCircle, Clock, Brain, Users, Target, TrendingUp, Heart, Sparkles } from "lucide-react";

const DoesItWork = () => {
  const tocItems = [
    { id: "structure-not-magic", title: "Pomodoro Isn't Magic — It's Structure" },
    { id: "structured-breaks", title: "Structured Breaks Beat Unplanned Breaks" },
    { id: "reduce-procrastination", title: "Pomodoro Helps Reduce Procrastination" },
    { id: "focus-quality", title: "Focus Quality Improves — Not Just Time Spent" },
    { id: "stress-burnout", title: "Students Report Lower Stress and Burnout" },
    { id: "consistency", title: "Pomodoro Works Best When Used Consistently" },
    { id: "with-others", title: "Why Pomodoro Works Even Better with Others" },
    { id: "csw-research", title: "Where CSW Fits into the Research" },
    { id: "faq", title: "FAQ" },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Does the Pomodoro Technique actually work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, research supports Pomodoro's effectiveness. It works because it combines well-researched principles like timeboxing, single-task focus, and planned breaks. These individually proven strategies, when combined, reliably improve productivity and focus."
        }
      },
      {
        "@type": "Question",
        "name": "Why do planned breaks improve performance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Studies on attention and fatigue show that people who work continuously until they feel tired experience sharper drops in performance than those who take short, scheduled breaks. Pomodoro forces breaks before focus collapses, keeping you mentally fresh."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro reduce procrastination?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Procrastination is about avoidance, not laziness. Research shows that short, defined commitments reduce avoidance behavior. Pomodoro reframes studying from 'I need to study for hours' to 'I'll focus for 25 minutes,' which is psychologically easier to start."
        }
      },
      {
        "@type": "Question",
        "name": "Does Pomodoro help with stress and burnout?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. University-based studies have found that structured focus systems like Pomodoro lower perceived stress, reduce burnout, and improve motivation. Knowing a break is coming reduces anxiety, and knowing you don't have to study forever makes effort feel manageable."
        }
      },
      {
        "@type": "Question",
        "name": "Is it better to use Pomodoro alone or with others?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Research on co-working and peer presence shows that working alongside others—even silently—increases persistence and reduces disengagement. Pomodoro becomes more powerful in study streams and virtual study rooms where shared start/stop times remove the need to self-police."
        }
      },
      {
        "@type": "Question",
        "name": "How often should I use the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Consistency beats intensity. Research on habit formation shows that consistent, repeated behavior in a stable structure leads to better long-term outcomes than sporadic intense effort. Students who use Pomodoro daily—even for short sessions—procrastinate less and feel more in control."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Does the Pomodoro Technique Actually Work? What Research (and Real Students) Say",
    "description": "Discover what research says about the Pomodoro Technique's effectiveness. Learn why timeboxing, structured breaks, and single-task focus reliably improve productivity and reduce procrastination.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/does-it-work/"
    },
    "keywords": ["pomodoro technique", "does pomodoro work", "productivity research", "timeboxing studies", "focus techniques", "procrastination research", "study methods evidence", "study stream", "online workspace", "virtual study room", "focus timer", "productivity method", "time management research", "study with me", "online study platform", "collaborative study"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Does the Pomodoro Technique Actually Work? | Research & Evidence | CSW",
    "description": "Discover what research says about the Pomodoro Technique's effectiveness. Learn why timeboxing, structured breaks, and single-task focus reliably improve productivity.",
    "url": "https://csw.live/pomodoro-technique/does-it-work/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Pomodoro Technique Research"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Productivity Research" },
      { "@type": "Thing", "name": "Timeboxing" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Procrastination Research" },
      { "@type": "Thing", "name": "Study Methods" },
      { "@type": "Thing", "name": "Time Management" },
      { "@type": "Thing", "name": "Ariga and Lleras Research" },
      { "@type": "Thing", "name": "Single-Task Focus" }
    ],
    "keywords": "pomodoro technique, does pomodoro work, productivity research, timeboxing, focus techniques, procrastination research, study stream, online workspace, virtual study room, focus timer, time management, study methods"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Does It Work?",
        "item": "https://csw.live/pomodoro-technique/does-it-work/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Pomodoro 101: What It Really Is",
      description: "A straightforward breakdown of the Pomodoro Technique.",
      href: "/pomodoro-technique/pomodoro-101/",
      icon: Clock
    },
    {
      title: "The Science of Focus",
      description: "Why timed focus intervals and breaks improve attention.",
      href: "/pomodoro-technique/science-of-focus/",
      icon: Brain
    },
    {
      title: "Building Better Study Habits",
      description: "How Pomodoro creates consistency that lasts.",
      href: "/pomodoro-technique/study-habits/",
      icon: Target
    },
    {
      title: "Pomodoro for Exam Prep",
      description: "Strategic studying for test success.",
      href: "/pomodoro-technique/exam-prep/",
      icon: Target
    },
    {
      title: "Active Learning with Pomodoro",
      description: "Transform passive reading into real understanding.",
      href: "/pomodoro-technique/active-learning/",
      icon: Brain
    },
    {
      title: "Avoid Burnout with Pomodoro",
      description: "Sustainable studying that protects your energy.",
      href: "/pomodoro-technique/avoid-burnout/",
      icon: Heart
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Does the Pomodoro Technique Actually Work? | Research & Evidence"
        description="Discover what research says about the Pomodoro Technique. Learn why timeboxing, structured breaks, and single-task focus reliably improve productivity and reduce procrastination."
        canonicalUrl="/pomodoro-technique/does-it-work/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Does It Work?</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" />
                <span>Research</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Does the Pomodoro Technique{" "}
                <span className="text-primary">Actually Work?</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                What research (and real students) say about the world's most popular focus technique.
              </p>
            </div>
          </div>
        </section>
      
      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <aside className="lg:col-span-1">
                <BlogTableOfContents items={tocItems} />
              </aside>
              
              {/* Article Content */}
              <article className="lg:col-span-3 prose prose-lg max-w-none">
                <div className="bg-muted/30 rounded-xl p-6 mb-8 border border-border">
                  <p className="text-lg text-muted-foreground m-0">
                    The Pomodoro Technique sounds almost <em>too</em> simple. Work for 25 minutes. Take a short break. Repeat. So it's fair to ask the obvious question: <strong>Does this actually work — or is it just another productivity trend?</strong>
                  </p>
                  <p className="text-lg text-foreground mt-4 mb-0 font-medium">
                    The short answer: <span className="text-primary">yes, it works</span>, but not for the reasons people usually think.
                  </p>
                </div>
                
                {/* Section 1 */}
                <div id="structure-not-magic" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Pomodoro Isn't Magic — It's Structure</h2>
                  </div>
                  <p>
                    There's no single study that says "Pomodoro works" in isolation. Instead, Pomodoro is supported by <strong>multiple well-researched principles</strong> that, when combined, reliably improve productivity and focus.
                  </p>
                  <p>Those principles include:</p>
                  <ul className="space-y-2">
                    <li><strong>Timeboxing</strong> — committing to defined work periods</li>
                    <li><strong>Single-task focus</strong> — eliminating multitasking</li>
                    <li><strong>Planned breaks</strong> — scheduled rest before fatigue sets in</li>
                    <li><strong>Reduced decision fatigue</strong> — following a preset rhythm</li>
                  </ul>
                  <p>
                    Each of these has strong research behind it. Pomodoro simply packages them into a usable system.
                  </p>
                </div>
                
                {/* Section 2 */}
                <div id="structured-breaks" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Structured Breaks Beat Unplanned Breaks</h2>
                  </div>
                  <p>
                    One of the strongest findings in productivity research is this: <strong>Planned breaks improve performance more than breaks taken randomly.</strong>
                  </p>
                  <p>
                    Studies on attention and fatigue show that people who work continuously until they "feel tired" experience sharper drops in performance than those who take short, scheduled breaks. Research by{" "}
                    <a 
                      href="https://pubmed.ncbi.nlm.nih.gov/21211793/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Ariga and Lleras (2011)
                    </a>{" "}
                    demonstrates that brief diversions from a task can dramatically improve focus.
                  </p>
                  <p>
                    Pomodoro forces breaks <em>before</em> focus collapses. That alone explains why many students feel more mentally fresh using Pomodoro — even when total study time is the same or shorter.
                  </p>
                </div>
                
                {/* Section 3 */}
                <div id="reduce-procrastination" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Pomodoro Helps Reduce Procrastination</h2>
                  </div>
                  <p>
                    Procrastination isn't about laziness. It's about <strong>avoidance</strong>.
                  </p>
                  <p>
                    Research in behavioral psychology, including{" "}
                    <a 
                      href="https://psycnet.apa.org/record/2007-01416-002" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Steel's meta-analysis (2007)
                    </a>{" "}
                    on the nature of procrastination, shows that <strong>short, defined commitments</strong> reduce avoidance behavior and increase task initiation.
                  </p>
                  <div className="bg-primary/5 border-l-4 border-primary p-4 my-6">
                    <p className="m-0 italic">
                      Pomodoro reframes studying from "I need to study for hours" to "I'll focus for 25 minutes."
                    </p>
                  </div>
                  <p>
                    For students who struggle to start, that difference is huge. In diary-based studies, a majority of students reported reduced procrastination behaviors when using structured time intervals similar to Pomodoro.
                  </p>
                </div>
                
                {/* Section 4 */}
                <div id="focus-quality" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Brain className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Focus Quality Improves — Not Just Time Spent</h2>
                  </div>
                  <p>
                    One common misconception is that Pomodoro is about studying <em>more</em>. In reality, it's about studying <strong>better</strong>.
                  </p>
                  <p>
                    Research on multitasking and task-switching, including findings by{" "}
                    <a 
                      href="https://www.pnas.org/doi/10.1073/pnas.0903620106" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Ophir et al. (2009)
                    </a>, shows that divided attention reduces learning, increases errors, and slows progress — even when people think they're being productive.
                  </p>
                  <p>Pomodoro minimizes task-switching by:</p>
                  <ul className="space-y-2">
                    <li>Limiting session length</li>
                    <li>Enforcing one-task focus</li>
                    <li>Creating a clear stop point</li>
                  </ul>
                  <p>
                    Students often report finishing tasks faster <em>because</em> they're less distracted.
                  </p>
                </div>
                
                {/* Section 5 */}
                <div id="stress-burnout" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Students Report Lower Stress and Burnout</h2>
                  </div>
                  <p>
                    Several university-based studies and wellness programs have found that structured focus systems like Pomodoro:
                  </p>
                  <ul className="space-y-2">
                    <li>Lower perceived stress</li>
                    <li>Reduce burnout — learn more in our guide to <Link to="/pomodoro-technique/avoid-burnout/" className="text-primary hover:underline">avoiding burnout with Pomodoro</Link></li>
                    <li>Improve motivation during long study periods</li>
                  </ul>
                  <p>
                    Research on{" "}
                    <a 
                      href="https://psycnet.apa.org/record/1998-01923-011" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      ego depletion and willpower (Baumeister et al., 1998)
                    </a>{" "}
                    suggests that knowing a break is coming reduces anxiety. Knowing you don't have to "study forever" makes effort feel manageable.
                  </p>
                  <p>
                    That emotional effect matters just as much as efficiency.
                  </p>
                </div>
                
                {/* Section 6 */}
                <div id="consistency" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Pomodoro Works Best When Used Consistently</h2>
                  </div>
                  <p>
                    Pomodoro is not a one-time trick.
                  </p>
                  <p>
                    Research on habit formation, including the landmark study by{" "}
                    <a 
                      href="https://onlinelibrary.wiley.com/doi/abs/10.1002/ejsp.674" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Lally et al. (2010)
                    </a>, shows that <strong>consistent, repeated behavior in a stable structure</strong> leads to better long-term outcomes than sporadic intense effort.
                  </p>
                  <p>Students who use Pomodoro daily — even for short sessions — tend to:</p>
                  <ul className="space-y-2">
                    <li>Procrastinate less</li>
                    <li>Study more consistently</li>
                    <li>Feel more in control of their workload</li>
                  </ul>
                  <div className="bg-muted/50 rounded-lg p-4 my-6">
                    <p className="m-0 font-medium text-foreground">
                      💡 Consistency beats intensity every time.
                    </p>
                  </div>
                </div>
                
                {/* Section 7 */}
                <div id="with-others" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Why Pomodoro Works Even Better with Others</h2>
                  </div>
                  <p>
                    Pomodoro becomes more powerful when paired with <strong>shared focus environments</strong>.
                  </p>
                  <p>
                    Research on co-working and peer presence, including findings by{" "}
                    <a 
                      href="https://www.iza.org/publications/dp/2004" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Falk and Ichino (2006)
                    </a>, shows that working alongside others — even silently — increases persistence and reduces disengagement.
                  </p>
                  <p>That's why Pomodoro is so effective in:</p>
                  <ul className="space-y-2">
                    <li><Link to="/study-stream/" className="text-primary hover:underline">Study streams</Link></li>
                    <li><Link to="/study-room/" className="text-primary hover:underline">Virtual study rooms</Link></li>
                    <li>Silent co-working sessions</li>
                  </ul>
                  <p>
                    Shared start and stop times remove the need to self-police.
                  </p>
                </div>
                
                {/* Section 8 */}
                <div id="csw-research" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Where CSW Fits into the Research</h2>
                  </div>
                  <p>
                    Platforms like <strong>csw.live</strong> apply these research-backed principles by combining:
                  </p>
                  <ul className="space-y-2">
                    <li><strong>Built-in timers</strong> using Pomodoro intervals</li>
                    <li><strong>Shared focus sessions</strong> with visible peer presence</li>
                    <li><strong>Consistent structure</strong> that builds daily habits</li>
                  </ul>
                  <p>
                    This isn't accidental. CSW was built around the idea that focus tools should work <em>with</em> how the brain actually functions — not against it.
                  </p>
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-8">
                    <h3 className="text-xl font-bold text-foreground mb-3">The Bottom Line</h3>
                    <p className="m-0">
                      Pomodoro works — not because it's trendy, but because it aligns with how attention, motivation, and habit formation actually function. When combined with social accountability and consistent use, it becomes one of the most effective study tools available.
                    </p>
                  </div>
                </div>
                
                {/* FAQ Section */}
                <div id="faq" className="scroll-mt-24 mt-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">Frequently Asked Questions</h2>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="bg-muted/30 rounded-lg p-6 border border-border">
                        <h3 className="text-lg font-semibold text-foreground mb-3">{faq.name}</h3>
                        <p className="text-muted-foreground m-0">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
      </main>
      
      {/* Related Articles */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <Link 
                  key={index}
                  to={article.href}
                  className="group bg-background rounded-xl p-6 border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <article.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {article.description}
                  </p>
                  <span className="inline-flex items-center text-primary text-sm font-medium">
                    Read more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Try the Pomodoro Technique?
            </h2>
            <p className="text-muted-foreground mb-8">
              Join thousands of students using Pomodoro timers with built-in study streams at CSW.
            </p>
            <a 
              href="https://csw.live" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-colors"
            >
              Start Studying Now <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DoesItWork;
